import { MondayWorkspace } from "shared/datamodel/schemas/monday-workspace";
import { concat, omit, prop, uniqBy } from "rambda";

export const getWorkspacesForUser = async ({
  page,
  limit,
  fetcher,
  logger = console,
}: {
  page: number;
  limit: number;
  fetcher: (page: number, limit: number) => Promise<MondayWorkspace[]>;
  logger: typeof console;
}) => {
  try {
    const MAX_PAGES = 100;
    const LIMIT = 500;
    const PARALLEL_REQUESTS = 3;
    let allWorkspaces: MondayWorkspace[] = [];
    let currentPage = page;

    // Helper to fetch a single page
    const fetchPage = (page: number) => fetcher(page, limit);

    while (currentPage <= MAX_PAGES) {
      // Prepare up to parallelRequests requests in parallel
      const pagesToFetch: number[] = [];
      for (let index = 0; index < PARALLEL_REQUESTS && currentPage + index <= MAX_PAGES; index++) {
        pagesToFetch.push(currentPage + index);
      }

      const results = await Promise.all(pagesToFetch.map(fetchPage));
      const workspacesResults = results.flat().filter((workspace) => !!workspace.name);
      // Flatten and add to allWorkspaces
      allWorkspaces = uniqBy(prop("id"), concat(allWorkspaces, workspacesResults));

      // If any page returned less than LIMIT, we reached the end
      if (results.some((workspaces) => workspaces.length < LIMIT)) {
        break;
      }

      currentPage += pagesToFetch.length;

      if (allWorkspaces.length >= limit) {
        break;
      }
    }

    return { workspaces: allWorkspaces, cursor: currentPage };
  } catch (error) {
    logger.error("error while calculating workspaces", { error });
    throw error;
  }
};

export const recombineBoards = (
  boards: {
    id: string;
    name: string;
    type: string;
    columns: { settings_str: string; type?: string; title?: string }[];
  }[],
  withColumns = false
) => {
  const childToParentMapping = new Map<string, string>();
  for (const board of boards) {
    if (board.type === "board") {
      try {
        const subItemsBoard = board.columns.find((column) => column?.settings_str.includes("boardIds"));
        const settings = JSON.parse(subItemsBoard?.settings_str ?? `{"boardIds": []}`);
        settings.boardIds.forEach((boardId: string) => {
          childToParentMapping.set(boardId.toString(), board.id);
        });
      } catch (error) {
        console.error("error while parsing settings for board", { error, boardId: board.id });
      }
    }
  }
  return boards.map((board) => {
    const parentId = childToParentMapping.get(board.id.toString());
    if (parentId) {
      return {
        parentId,
        ...board,
      };
    }
    return withColumns ? board : omit(["columns"], board);
  });
};
